import { useNavigate } from "react-router-dom";
import { useState, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Reaptcha from "reaptcha";
import heroBanner from "../../assets/images/page-title.jpg";
import ShellCard from "../../components/ShellCard";

import { HTTP } from "../../http/http-common";

const ContactUs = ({ settings }) => {
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);
  const navigate = useNavigate();

  const verify = () => {
    captchaRef.current.getResponse().then((res) => {
      setCaptchaToken(res);
    });
  };

  const expired = () => {
    captchaRef.current.reset();
    setCaptchaToken(null);
  };

  const handleNameChange = (e) => {
    const value = e.target.value;

    if (value === "" || /^[a-zA-Z\s]*$/.test(value)) {
      formik.setFieldValue("name", value);
    }
  };

  const handlePhoneChange = (e) => {
    const input = e.target.value.replace(/\D/g, "");

    if (input.length <= 10) {
      formik.setFieldValue("phone", input);
    }
  };

  const formatPhoneDisplay = (value) => {
    if (!value) return "";
    const cleaned = value.replace(/\D/g, "");

    if (cleaned.length <= 3) {
      return cleaned;
    } else if (cleaned.length <= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    } else {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
        6,
        10
      )}`;
    }
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      phone: "",
      comments: "",
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .matches(/^[a-zA-Z\s]+$/, "Name must contain only letters")
        .required("Name is required!"),
      email: Yup.string()
        .matches(
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          "Invalid email format"
        )
        .required("Email is required!"),
      phone: Yup.string()
        .matches(/^[0-9]{10}$/, "Phone must be exactly 10 digits")
        .required("Phone is required!"),
      comments: Yup.string()
        .min(10, "Minimum 10 characters")
        .max(200, "Maximum 200 characters")
        .required("Comments is required!"),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values, { resetForm }) => {
      // Validate captcha before submission
      if (!captchaToken) {
        setInfo("Please complete the captcha verification!");
        return;
      }

      setLoading(true);
      setInfo(null);

      HTTP.post("/contactus", { ...values, captchaToken })
        .then((res) => {
          setInfo(res.message || "Message sent successfully!");
          captchaRef.current.reset();
          setCaptchaToken(null);
          resetForm({ values: "" });
          setLoading(false);
        })
        .catch((err) => {
          setInfo("Error sending message. Please try again.");
          setLoading(false);
        });
    },
  });

  return (
    <>
      {!loading && (
        <section className="hero-slider-section">
          <div
            className="hero-banner"
            style={{
              backgroundImage: `url(${heroBanner})`,
            }}
          >
            <div className="banner-text-container">
              <div className="banner-heading">
                <h1 className="banner-title">Contact Us</h1>
                <p className="banner-subheading">Metro Traffic School</p>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="section-padding" style={{ marginBottom: "57px" }}>
        <div className="container">
          <div className="row">
            <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-8 col-xxl-8 offset-xl-2">
              <div className="phone-number mb-md-3 mb-3 mb-xl-4">
                <p className="text-warning fs-4 pt-5 mb-0 text-center">
                  <a
                    href="tel:305-513-0063"
                    className="text-warning text-decoration-none"
                  >
                    (305) 513-0063
                  </a>
                  {" - "}
                  <a
                    href="tel:1-888-41-METRO"
                    className="text-warning text-decoration-none"
                  >
                    1-888-41-METRO
                  </a>
                </p>
              </div>
              <ShellCard>
                <div className="mb-4 px-3">
                  <h3 className="form-heading mb-1 text-capitalize text-warning">
                    Send us a Message
                  </h3>
                  <p className="color-white mb-0" style={{ color: "#fff" }}>
                    Please email us with your questions and comments.
                  </p>
                </div>
                <div className="px-3">
                  {info && (
                    <h4
                      className={`mb-3 ${info.includes("Error") || info.includes("Please complete")
                          ? "text-warning"
                          : "text-success"
                        }`}
                    >
                      {info}
                    </h4>
                  )}
                </div>
                <form onSubmit={formik.handleSubmit}>
                  <div className="row">
                    <div className="col-12 col-sm-12 col-md-12 col-lg-6 col-xl-6 col-xxl-6 mb-4">
                      <input
                        id="name"
                        type="text"
                        className="form-control"
                        placeholder="Name"
                        aria-label="Name"
                        value={formik.values.name}
                        onChange={handleNameChange}
                        onBlur={formik.handleBlur}
                        style={{
                          backgroundColor: "rgba(255, 255, 255, 0.1)",
                          color: "#fff",
                          borderColor:
                            formik.errors.name && formik.touched.name
                              ? "var(--metro-yellow)"
                              : "rgba(255, 255, 255, 0.2)",
                        }}
                      />
                      {formik.errors.name && formik.touched.name && (
                        <p className="text-warning mt-2">
                          {formik.errors.name}
                        </p>
                      )}
                    </div>
                    <div className="col-12 col-sm-12 col-md-12 col-lg-6 col-xl-6 col-xxl-6 mb-4">
                      <input
                        id="email"
                        type="text"
                        className="form-control"
                        placeholder="Email"
                        aria-label="Email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        style={{
                          backgroundColor: "rgba(255, 255, 255, 0.1)",
                          color: "#fff",
                          borderColor:
                            formik.errors.email && formik.touched.email
                              ? "var(--metro-yellow)"
                              : "rgba(255, 255, 255, 0.2)",
                        }}
                      />
                      {formik.errors.email && formik.touched.email && (
                        <p className="text-warning mt-2">
                          {formik.errors.email}
                        </p>
                      )}
                    </div>
                    <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12 col-xxl-12 mb-4">
                      <input
                        id="phone"
                        type="text"
                        className="form-control"
                        placeholder="Phone (XXX) XXX-XXXX"
                        aria-label="Phone"
                        value={formatPhoneDisplay(formik.values.phone)}
                        onChange={handlePhoneChange}
                        onBlur={formik.handleBlur}
                        style={{
                          backgroundColor: "rgba(255, 255, 255, 0.1)",
                          color: "#fff",
                          borderColor:
                            formik.errors.phone && formik.touched.phone
                              ? "var(--metro-yellow)"
                              : "rgba(255, 255, 255, 0.2)",
                        }}
                      />
                      {formik.errors.phone && formik.touched.phone && (
                        <p className="text-warning mt-2">
                          {formik.errors.phone}
                        </p>
                      )}
                    </div>
                    <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12 col-xxl-12 mb-4">
                      <textarea
                        className="form-control"
                        id="comments"
                        rows="3"
                        placeholder="Comments"
                        value={formik.values.comments}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        style={{
                          backgroundColor: "rgba(255, 255, 255, 0.1)",
                          color: "#fff",
                          borderColor:
                            formik.errors.comments && formik.touched.comments
                              ? "var(--metro-yellow)"
                              : "rgba(255, 255, 255, 0.2)",
                        }}
                      ></textarea>
                      {formik.errors.comments && formik.touched.comments && (
                        <p className="text-warning mt-2">
                          {formik.errors.comments}
                        </p>
                      )}
                    </div>
                    <div className="col-12 col-sm-6 col-md-6  col-lg-6 col-xl-6 col-xxl-6 mb-3 mb-md-3">
                      {import.meta.env.VITE_SITE_KEY && (
                        <Reaptcha
                          sitekey={import.meta.env.VITE_SITE_KEY}
                          ref={captchaRef}
                          onVerify={verify}
                          onExpire={expired}
                        />
                      )}
                    </div>
                    <div className="col-12 col-sm-6 col-md-6  col-lg-6 col-xl-6 col-xxl-6 d-flex align-items-center justify-content-end">
                      <button
                        type="submit"
                        className="btn btn-lg px-4 py-3 fw-bold text-dark metro-btn-primary metro-btn-pulse"
                      >
                        Send Message
                        <i className="fa-solid fa-circle-chevron-right"></i>
                      </button>
                    </div>
                  </div>
                </form>
              </ShellCard>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactUs;
